import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getArticleBySlug, getRecentArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import ArticleCard from '@/components/base/ArticleCard';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = article
    ? getRecentArticles(3).filter((a) => a.slug !== article.slug)
    : [];

  const categoryInfo = article
    ? categories.find((c) => c.slug === article.category)
    : null;

  const categoryLabel = article
    ? article.category === 'zonghe-fuwu'
      ? '综合服务'
      : article.category === 'jishu-zhichi'
        ? '技术支持'
        : article.category === 'shangwu-hezuo'
          ? '商务合作'
          : article.category === 'xingye-zixun'
            ? '行业资讯'
            : article.category === 'chanpin-dongtai'
              ? '产品动态'
              : '成功案例'
    : '';

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - aqdlt 官方平台`;
    }
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    const existingScript = document.getElementById('article-jsonld');
    if (existingScript) existingScript.remove();

    if (article) {
      const script = document.createElement('script');
      script.id = 'article-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        author: {
          '@type': 'Organization',
          name: article.author,
        },
        datePublished: article.publishDate,
        dateModified: article.publishDate,
        image: article.image,
        url: `https://aqdlt.com/article/${article.slug}`,
        publisher: {
          '@type': 'Organization',
          name: 'aqdlt',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://aqdlt.com/article/${article.slug}`,
        },
      });
      document.head.appendChild(script);
    }

    return () => {
      const s = document.getElementById('article-jsonld');
      if (s) s.remove();
    };
  }, [article, slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h2>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb
            items={[
              { label: categoryLabel, path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700">
                  {categoryLabel}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="inline-block px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime}
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="relative w-full h-64 md:h-96 rounded-lg overflow-hidden mb-10">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-full object-cover"
                title={`${article.title} - aqdlt`}
              />
            </div>

            {/* Content */}
            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Article Footer */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 mr-2">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-block px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 cursor-pointer hover:bg-secondary-200 transition-colors"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200/70">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">相关文章</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.slug} article={ra} />
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}